<script setup lang="ts">
import {computed, onBeforeUnmount, ref, watch} from 'vue';
import {RouterLink} from 'vue-router';
import {useI18n} from 'vue-i18n';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import type {SnackbarAction} from '@/ts/store/snackbar-store';

/**
 * A confirmation that something the reader did has worked, with what to do next (IPD-3328), drawn
 * as the design system's Snackbar with text actions: the way onward, a Dismiss, or both.
 *
 * Only what is seen lives here. The message is not announced by this component: whoever holds it
 * keeps a permanent live region, because one inserted together with its content is not reliably
 * read out (see snackbar-host).
 */
const props = defineProps<{
  message: string;
  action: SnackbarAction | null;
  dismissible: boolean;
  durationMs: number;
}>();

const emit = defineEmits<{
  /** The countdown ran out or the reader dismissed it. */
  close: [];
  /** The reader took the action, which leaves the page the snackbar was confirming. */
  navigate: [];
}>();

const {t: $t} = useI18n();

// The design keeps a short message with a single text action on one line, and moves the actions
// under the message once there are two of them.
const stacked = computed<boolean>(() => Boolean(props.action) && props.dismissible);

/**
 * The countdown is "of no user interaction", which a plain timeout does not deliver: it would take
 * the snackbar away from under the pointer of somebody on their way to its action. It is held while
 * the pointer is over it or something in it has focus, and restarts once neither is.
 *
 * The hold starts at the first pointer movement, not at the appearance: a snackbar raised directly
 * under a cursor that simply stays where it was holds nothing.
 */
const pointerOn = ref<boolean>(false);
const focused = ref<boolean>(false);
// Either one holds it: a mouse leaving must not cancel a hold the keyboard is still asserting.
const held = computed<boolean>(() => pointerOn.value || focused.value);
let timer: number | null = null;

function clearTimer(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
}

watch([held, (): number => props.durationMs], ([isHeld, durationMs]): void => {
  clearTimer();
  if (isHeld) {
    return;
  }
  timer = window.setTimeout(() => {
    emit('close');
  }, durationMs);
}, {immediate: true});

onBeforeUnmount(clearTimer);
</script>

<template>
  <!--
    No live role and no `aria-hidden` here: a plain element appearing is not announced, so the
    holder's region speaks once, and the controls inside stay in the accessibility tree — hiding a
    surface that holds a tab stop would leave a keyboard user on a control their reader never names.
  -->
  <div
    class="balm-snackbar"
    :class="{'bs-stacked': stacked}"
    @pointermove="pointerOn = true"
    @pointerleave="pointerOn = false"
    @focusin="focused = true"
    @focusout="focused = false"
  >
    <div class="bs-body">
      <span class="bs-icon" aria-hidden="true">
        <svg-illustration name="sign-up/check-circle.svg"/>
      </span>
      <!-- Hidden from readers, not from eyes: the holder's region already speaks this line. -->
      <span class="bs-text" aria-hidden="true">{{ message }}</span>
    </div>
    <div v-if="action || dismissible" class="bs-actions">
      <router-link v-if="action" :to="action.to" class="bs-text-action" @click="emit('navigate')">
        <span>{{ action.label }}</span>
        <svg-illustration name="arrow-right.svg" class="bs-arrow"/>
      </router-link>
      <span v-if="action && dismissible" class="bs-divider" aria-hidden="true"/>
      <button v-if="dismissible" type="button" class="bs-text-action" @click="emit('close')">
        {{ $t('snackbar_dismiss') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.balm-snackbar {
  // The design system's inverse feedback surface: dark over a light page, light over a dark one.
  --bs-surface: var(--Neutral-UI-neutral-ui-100);
  --bs-ink: var(--Neutral-UI-neutral-ui-0);

  // The design system's spacing tokens are this app's own, under the same names: its specs are drawn
  // at the large-screen values, and the tokens scale down below that as the design system says.
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-4);
  border-radius: var(--border-radius);
  background: var(--bs-surface);
  color: var(--bs-ink);

  // 14px at every width, which no single token gives: Body is 14px until it grows on large screens,
  // where Body SM takes over at 14px. Both still follow the reader's text-size preference.
  @include font-body;
  line-height: 1.4;

  @include screen-lg {
    @include font-body-sm;
    line-height: 1.4;
  }
}

// Two actions move under the message, flush right.
.bs-stacked {
  flex-direction: column;
  align-items: stretch;
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-5) var(--Spacing-spacing-4) var(--Spacing-spacing-4);

  .bs-actions {
    justify-content: flex-end;
  }
}

.bs-body {
  display: flex;
  align-items: flex-start;
  gap: var(--Spacing-spacing-2);
  min-width: 0;
}

.bs-icon {
  flex: 0 0 auto;
  display: flex;
  // As tall as the message's first line and centred in it, rather than hung from its top edge.
  align-items: center;
  height: 1.4em;
  // The completed-state green, which keeps its value in both schemes: the brand green the dark
  // scheme swaps in for primary-2 is too pale against the light surface the snackbar takes there.
  color: var(--Functional-wl-completed-surface);

  :deep(svg) {
    width: 16px;
    height: 16px;
    // The design system's optical lift: centred on the line box the glyph reads a pixel low against
    // this typeface's text.
    transform: translateY(-1px);
  }
}

.bs-text {
  min-width: 0;
  overflow-wrap: break-word;
}

.bs-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-3);
}

.bs-text-action {
  @include focus-visible;
  @include font-body-bold;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
  padding: 0;
  border: none;
  background: none;
  color: var(--bs-ink);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  @include screen-lg {
    @include font-body-sm-bold;
  }

  &:hover {
    opacity: 0.75;
  }

  // A 44px target without the height the design does not draw: the hit area reaches past the
  // label, the layout does not.
  &::before {
    content: '';
    position: absolute;
    inset: 50% -8px auto;
    height: 44px;
    transform: translateY(-50%);
  }
}

.bs-arrow {
  display: flex;

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}

.bs-divider {
  width: 1px;
  height: 12px;
  background: color-mix(in srgb, var(--bs-ink) 40%, transparent);
}
</style>
